import React, { useState, useCallback, useMemo } from 'react';
import { Box } from '@atlaskit/primitives';
import { useEntryPointButtonTrigger } from '@atlassian/jira-entry-point-button-trigger/src/index.tsx';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type { CropProperties } from '@atlassian/jira-navigation-apps-sidebar-inline-config-project-header/src/common/types.tsx';
import type { PredefinedAvatar } from '@atlassian/jira-navigation-apps-sidebar-inline-config-project-header/src/services/use-avatars/index.tsx';
import { EditableAvatar } from '@atlassian/jira-navigation-apps-sidebar-inline-config-project-header/src/ui/editable-avatar/index.tsx';
import { JiraPopup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import messages from '../messages.tsx';
import { navigationKitAvatarPopupContentEntrypoint } from './entrypoint.tsx';

type NavigationKitAvatarPopupProps = {
	canEdit: boolean;
	projectId: number;
	selectedAvatarUrl: string;
	// TODO: JGFENG-1029 - Clean up legacy props
	systemAvatars?: Array<PredefinedAvatar>;
	latestCustomAvatar?: PredefinedAvatar;
	onUploadAvatar?: (
		file: File,
		crop: CropProperties,
		onError: (errorState: boolean) => void,
	) => Promise<void>;
	onSelectAvatar?: (
		avatarId: number,
		avatarUrl: string,
		onError: (errorState: boolean) => void,
	) => void;
};

export const NavigationKitAvatarPopup = ({
	canEdit,
	projectId,
	selectedAvatarUrl,
	// TODO: JGFENG-1029 - Clean up legacy props
	systemAvatars,
	latestCustomAvatar,
	onSelectAvatar,
	onUploadAvatar,
}: NavigationKitAvatarPopupProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const { formatMessage } = useIntl();
	const onAvatarClick = useCallback(() => {
		setIsOpen(true);
	}, [setIsOpen]);
	const onClose = useCallback(() => {
		setIsOpen(false);
	}, [setIsOpen]);
	const selectedAvatarId = Number(
		selectedAvatarUrl.substring(selectedAvatarUrl.lastIndexOf('/') + 1),
	);
	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		navigationKitAvatarPopupContentEntrypoint,
		{},
	);
	const entrypointTrigger = useEntryPointButtonTrigger(entryPointActions);

	const runtimeProps = useMemo(
		() =>
			fg('jgfeng_fetch_system_avatars_on_demand')
				? { onClose, projectId, selectedAvatarId, selectedAvatarUrl }
				: {
						latestCustomAvatar,
						onClose,
						onSelectAvatar,
						onUploadAvatar,
						projectId,
						selectedAvatarId,
						systemAvatars,
						selectedAvatarUrl,
					},
		[
			onClose,
			projectId,
			selectedAvatarId,
			selectedAvatarUrl,
			latestCustomAvatar,
			onSelectAvatar,
			onUploadAvatar,
			systemAvatars,
		],
	);

	return (
		<JiraPopup
			messageId="horizontal-nav.ui.content.header.project-header.project-avatar.navigation-kit-avatar-popup.jira-popup.popup"
			messageType="transactional"
			placement="bottom-start"
			role="dialog"
			label={formatMessage(messages.title)}
			isOpen={isOpen}
			onClose={onClose}
			content={() => (
				<JiraEntryPointContainer
					id="navigation-kit-avatar.popup-content"
					packageName="jiraNavigationKitAvatarPopupContent"
					entryPointReferenceSubject={entryPointReferenceSubject}
					runtimeProps={runtimeProps}
				/>
			)}
			trigger={(triggerProps) => {
				return (
					<Box ref={entrypointTrigger}>
						<EditableAvatar
							avatarUrl={selectedAvatarUrl}
							canEdit={canEdit}
							onClick={onAvatarClick}
							triggerProps={triggerProps}
						/>
					</Box>
				);
			}}
		/>
	);
};
